// Sortable data table with optional numbered rows and change flashing.

import { h, type Child } from "./dom";
import type { PanelApi } from "./registry";

export interface Col<T> {
  label: string;
  /// Sort value and default display.
  get: (r: T) => unknown;
  /// Display; defaults to the value as text.
  fmt?: (v: never, r: T) => Child;
  /// Extra cell class, e.g. up/down coloring.
  cls?: (v: never, r: T) => string;
  num?: boolean;
  sort?: boolean;
  title?: string;
}

export interface TableOpts<T> {
  /// Number rows and make them actions: the command to run, or a callback.
  action?: (r: T) => string | (() => void) | null;
  panel?: PanelApi;
  /// Stable row identity, enables flashing cells whose text changed.
  key?: (r: T) => string;
  /// Rows that are group headings rather than data.
  group?: (r: T) => string | null;
  sortable?: boolean;
  /// Extra row class, e.g. to highlight the loaded security.
  rowClass?: (r: T) => string | undefined;
}

/// A table that can be re-rendered with fresh rows while keeping its sort
/// order and flashing changed cells.
export class DataTable<T> {
  readonly el = h("div");
  private sortCol = -1;
  private sortDir = 1;
  private rows: T[] = [];
  private foot: T[] = [];
  private prev = new Map<string, string>();
  /// Action numbers by row position, reused across re-renders.
  private slots: number[] = [];

  constructor(
    private cols: Col<T>[],
    private opts: TableOpts<T> = {},
  ) {}

  /// `foot` rows (totals, medians) stay below the data, unsorted.
  set(rows: T[], foot: T[] = []): void {
    this.rows = rows;
    this.foot = foot;
    this.render();
  }

  private cell(c: Col<T>, r: T): HTMLElement {
    const v = c.get(r);
    const shown = c.fmt ? c.fmt(v as never, r) : v === undefined || v === null ? "—" : String(v);
    return h("td", { class: [c.num ? "num" : "", c.cls?.(v as never, r) ?? ""].join(" ") }, shown);
  }

  private render(): void {
    const { cols, opts } = this;
    let rows = this.rows;
    if (this.sortCol >= 0 && !opts.group) {
      const get = cols[this.sortCol].get;
      rows = [...rows].sort((a, b) => cmp(get(a), get(b)) * this.sortDir);
    }
    const sortable = opts.sortable ?? !opts.group;
    const head = h(
      "tr",
      null,
      opts.action ? h("th", null, "") : null,
      ...cols.map((c, i) =>
        h(
          "th",
          {
            class: [c.num ? "num" : "", sortable && c.sort !== false ? "sort" : ""].join(" "),
            title: c.title,
            onclick: sortable && c.sort !== false ? () => this.sortBy(i) : undefined,
          },
          c.label + (i === this.sortCol ? (this.sortDir > 0 ? " ▲" : " ▼") : ""),
        ),
      ),
    );
    const body = h("tbody");
    const next = new Map<string, string>();
    let pos = 0;
    for (const r of rows) {
      const g = opts.group?.(r);
      if (g) {
        body.append(h("tr", { class: "group" }, h("td", { colspan: String(cols.length + (opts.action ? 1 : 0)) }, g)));
        continue;
      }
      const act = opts.action?.(r) ?? null;
      let n: number | null = null;
      if (act && opts.panel) {
        n = opts.panel.action(act, this.slots[pos]);
        this.slots[pos++] = n;
      }
      const tr = h("tr", { class: [act ? "click" : "", opts.rowClass?.(r) ?? ""].join(" ").trim() || undefined });
      if (act) {
        tr.addEventListener("click", (e) => {
          if ((e.target as HTMLElement).closest("a, button, input")) return;
          if (typeof act === "function") act();
          else if (e.shiftKey && opts.panel) opts.panel.goOther(act);
          else opts.panel?.go(act);
        });
      }
      if (opts.action) tr.append(h("td", { class: "nm num" }, n ? `${n})` : ""));
      const key = opts.key?.(r);
      cols.forEach((c, i) => {
        const v = c.get(r);
        const td = this.cell(c, r);
        if (key !== undefined && c.num) {
          const id = `${key}\u0000${i}`;
          const text = td.textContent ?? "";
          const old = this.prev.get(id);
          if (old !== undefined && old !== text && typeof v === "number") {
            td.classList.add(parseNum(text) >= parseNum(old) ? "flash-up" : "flash-down");
          }
          next.set(id, text);
        }
        tr.append(td);
      });
      body.append(tr);
    }
    if (opts.key) this.prev = next;
    for (let i = pos; i < this.slots.length; i++) opts.panel?.action(null, this.slots[i]);
    const foot = this.foot.length
      ? h(
          "tfoot",
          null,
          ...this.foot.map((r) => h("tr", { class: opts.rowClass?.(r) }, opts.action ? h("td") : null, ...cols.map((c) => this.cell(c, r)))),
        )
      : null;
    this.el.replaceChildren(h("table", { class: "dt" }, h("thead", null, head), body, foot));
  }

  private sortBy(i: number): void {
    if (this.sortCol === i) this.sortDir = -this.sortDir;
    else {
      this.sortCol = i;
      this.sortDir = this.cols[i].num ? -1 : 1;
    }
    this.render();
  }
}

function parseNum(s: string): number {
  return Number(s.replace(/[^0-9.-]/g, "")) || 0;
}

function cmp(a: unknown, b: unknown): number {
  const na = a === undefined || a === null || (typeof a === "number" && !isFinite(a));
  const nb = b === undefined || b === null || (typeof b === "number" && !isFinite(b));
  if (na || nb) return na === nb ? 0 : na ? 1 : -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b));
}
